/**
 * Badge-template thumbnail URLs + a "warm the cache" prefetcher.
 *
 * The /badge-templates gallery renders ~2,125 <img> tags (85 hero previews +
 * 2,040 colour swatches). On a cold cache that's a lot of image bytes to fetch
 * on first visit. Since the generated PNGs are served with a 1-year `immutable`
 * cache, we can warm them ahead of time from the landing page: by the time the
 * visitor clicks "Browse all templates", the images are already in the browser
 * cache and the gallery paints instantly.
 *
 * The URL helpers here are the SINGLE source of truth — the gallery imports the
 * same `idFor`/`thumbFor` so a prewarmed URL matches the gallery's <img src>
 * byte-for-byte (including the manifest's `?v=` content hash). If they drifted,
 * every prewarm would be a cache miss.
 */
import { TEMPLATE_STYLE_META, TEMPLATES } from '../components/badge-designer-utils';

export const ASSET = '/assets/badge-templates/generated';
export const idFor = (style: string, color: string) => `gen-${style}-${color}`;

// The manifest's `thumb` carries the ?v= content hash (busts the immutable cache
// on redesign); fall back to the plain path for any id missing from the manifest.
const THUMB_BY_ID = new Map(TEMPLATES.map((t) => [t.id, t.thumb]));
export const thumbFor = (style: string, color: string): string =>
  THUMB_BY_ID.get(idFor(style, color)) ?? `${ASSET}/${idFor(style, color)}-preview.png`;

/** One hero preview per style (its default colour) — the gallery's first paint. */
export function heroThumbUrls(): string[] {
  return Object.entries(TEMPLATE_STYLE_META).map(([style, m]) => thumbFor(style, m.default));
}

/** Every style×colour swatch, hero-first, in gallery render order (deduped). */
export function allThumbUrls(): string[] {
  const urls: string[] = [];
  for (const [style, m] of Object.entries(TEMPLATE_STYLE_META)) {
    urls.push(thumbFor(style, m.default));
    for (const color of m.colors) urls.push(thumbFor(style, color));
  }
  return [...new Set(urls)];
}

// ── connection guards ──────────────────────────────────────────────────────
// Never burn a visitor's metered/very-slow data on speculative prefetch.
type NetInfo = { saveData?: boolean; effectiveType?: string };
const netInfo = (): NetInfo | undefined =>
  (navigator as unknown as { connection?: NetInfo }).connection;

/** Any speculative fetching at all? (false on Data Saver / slow-2g.) */
function mayPrefetchAtAll(): boolean {
  const c = netInfo();
  if (c?.saveData) return false;
  if (c?.effectiveType === 'slow-2g') return false;
  return true;
}

/** The full 2,040-image tier — only on unmetered, non-2g connections. */
function mayPrefetchFullSet(): boolean {
  const c = netInfo();
  if (!c) return true; // unknown (most desktops) → allow
  if (c.saveData) return false;
  if (c.effectiveType && /2g$/.test(c.effectiveType)) return false; // 2g, slow-2g
  return true;
}

// ── the warmer ─────────────────────────────────────────────────────────────
function warmOne(url: string, signal?: AbortSignal): Promise<void> {
  if (typeof fetch === 'function') {
    // Low priority so it never competes with the landing page's own resources.
    // Consume the body (blob) so the response is fully committed to the cache.
    return fetch(url, { signal, cache: 'force-cache', priority: 'low' } as RequestInit)
      .then((r) => r.blob())
      .then(() => undefined)
      .catch(() => undefined);
  }
  return new Promise<void>((resolve) => {
    const img = new Image();
    img.onload = img.onerror = () => resolve();
    img.src = url;
  });
}

async function warmImages(
  urls: string[],
  { concurrency = 6, signal }: { concurrency?: number; signal?: AbortSignal } = {}
): Promise<void> {
  let i = 0;
  const worker = async () => {
    while (i < urls.length) {
      if (signal?.aborted) return;
      await warmOne(urls[i++], signal);
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, urls.length) }, worker));
}

// requestIdleCallback with a setTimeout fallback (Safari lacks it).
type IdleHandle = { cancel: () => void };
function scheduleIdle(fn: () => void): IdleHandle {
  const ric = (window as unknown as {
    requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number;
    cancelIdleCallback?: (h: number) => void;
  });
  if (typeof ric.requestIdleCallback === 'function') {
    const h = ric.requestIdleCallback(fn, { timeout: 3000 });
    return { cancel: () => ric.cancelIdleCallback?.(h) };
  }
  const h = window.setTimeout(fn, 1200);
  return { cancel: () => window.clearTimeout(h) };
}

/**
 * Warm the gallery thumbnails behind the scenes. Tier 1 (85 heroes) runs on any
 * allowed connection; Tier 2 (all 2,040 swatches) only on fast/unmetered ones.
 * Returns a cleanup fn that aborts in-flight warming (call it on unmount).
 */
export function prefetchGalleryThumbs(): () => void {
  const controller = new AbortController();
  if (!mayPrefetchAtAll()) return () => controller.abort();

  const idle = scheduleIdle(async () => {
    await warmImages(heroThumbUrls(), { concurrency: 6, signal: controller.signal });
    if (controller.signal.aborted || !mayPrefetchFullSet()) return;
    await warmImages(allThumbUrls(), { concurrency: 4, signal: controller.signal });
  });

  return () => {
    idle.cancel();
    controller.abort();
  };
}
